import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { useCallbackRef } from "@core-ui/hooks/useCallbackRef";
import t from "@ext/localization/locale/translate";
import FloatActions from "@ext/markdown/elements/float/edit/components/FloatActions";
import type { NodeViewProps } from "@tiptap/react";
import { GlassToolbarButton, GlassToolbarIcon, GlassToolbarSeparator } from "@ui-kit/GlassToolbar";
import { type ReactNode, useLayoutEffect, useState } from "react";
import { getBlockToolbarRegistry } from "./BlockToolbarRegistry";

interface BlockToolbarOptions {
	comment?: boolean;
	float?: boolean;
	delete?: boolean;
}

export const useBlockToolbar = (props: NodeViewProps, actions: ReactNode, options: BlockToolbarOptions = {}) => {
	const { editor, node, deleteNode } = props;
	const getPos = useCallbackRef(props.getPos);
	const [id] = useState(() => Symbol("block-toolbar"));
	const isEditable = editor?.isEditable;
	const apiUrlCreator = ApiUrlCreator.value;
	const hasComment = Boolean(node?.attrs.comment?.id);

	const handleComment = useCallbackRef(async () => {
		if (!editor || editor.isDestroyed) return;
		const pos = getPos();
		if (typeof pos !== "number") return;

		const target = editor.state.doc.nodeAt(pos);
		if (!target) return;

		editor.commands.setNodeSelection(pos);
		const position = { from: pos, to: pos + target.nodeSize };
		if (target.attrs.comment?.id) {
			editor.commands.openComment(target.attrs.comment.id, position);
			return;
		}

		const res = await FetchService.fetch(apiUrlCreator.getNewCommentId());
		if (!res.ok || editor.isDestroyed) return;

		const commentId = await res.text();
		if (editor.isDestroyed || editor.state.doc.nodeAt(pos) !== target) return;

		editor.commands.toggleComment({ id: commentId }, position);
	});

	const handleDelete = useCallbackRef(() => {
		if (!editor || editor.isDestroyed) return;
		deleteNode();
	});

	useLayoutEffect(() => {
		if (!editor || editor.isDestroyed || !isEditable) return;
		getBlockToolbarRegistry(editor).set(id, {
			getPos,
			groupActions: actions,
			actions: (
				<>
					{actions}
					{options.float && <FloatActions editor={editor} getPos={getPos} node={node} />}
					{(actions || options.float) && <GlassToolbarSeparator variant="inline" />}
					{options.comment && (
						<GlassToolbarButton
							aria-label={hasComment ? t("show-comment") : t("leave-comment")}
							onClick={handleComment}
							tooltipText={hasComment ? t("show-comment") : t("leave-comment")}
						>
							<GlassToolbarIcon icon={hasComment ? "message-square-text" : "message-square"} />
						</GlassToolbarButton>
					)}
					{options.delete !== false && (
						<GlassToolbarButton aria-label={t("delete")} onClick={handleDelete} tooltipText={t("delete")}>
							<GlassToolbarIcon icon="trash" />
						</GlassToolbarButton>
					)}
				</>
			),
		});
	}, [
		editor,
		id,
		isEditable,
		getPos,
		actions,
		node,
		options.float,
		options.comment,
		options.delete,
		hasComment,
		handleComment,
		handleDelete,
	]);

	useLayoutEffect(
		() => () => {
			if (editor) getBlockToolbarRegistry(editor).delete(id);
		},
		[editor, id],
	);
};
